import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTenant } from '../contexts/TenantContext';
import { useCart } from '../contexts/CartContext';
import { MEDICINE_TYPES } from '../services/inventoryApi';
import { getAds } from '../services/adApi';
import AdBanner from '../components/AdBanner';
import { API_BASE_URL as BASE_URL } from '../services/apiBaseUrl';

const fetchProduct = async (tenantId, id) => {
  const res = await fetch(`${BASE_URL}/pharmacy/products/${id}`, {
    headers: { 'Content-Type': 'application/json', 'X-Tenant-ID': tenantId },
  });
  if (!res.ok) throw new Error(`Failed to fetch product (${res.status})`);
  return res.json();
};

// ─── Stock badge ──────────────────────────────────────────────────────────────
const StockBadge = ({ active }) => (
  <span style={{
    display: 'inline-flex', alignItems: 'center', gap: 4,
    padding: '3px 9px', borderRadius: 9999, fontSize: 11, fontWeight: 600,
    fontFamily: 'DM Sans, sans-serif',
    background: active ? '#ecfdf5' : '#fef2f2',
    color: active ? '#065f46' : '#991b1b',
    border: `1px solid ${active ? '#6ee7b7' : '#fca5a5'}`,
    flexShrink: 0,
  }}>
    <span style={{ width: 5, height: 5, borderRadius: '50%', background: active ? '#10b981' : '#ef4444' }} />
    {active ? 'In Stock' : 'Out of Stock'}
  </span>
);

// ─── Section card ─────────────────────────────────────────────────────────────
const Card = ({ title, icon, children, style, accent }) => (
  <div style={{
    background: '#fff',
    borderRadius: 12,
    border: `1px solid ${accent ? accent + '33' : '#ebebf5'}`,
    borderTop: accent ? `3px solid ${accent}` : undefined,
    padding: '13px 15px',
    ...style,
  }}>
    {title && (
      <div style={{
        display: 'flex', alignItems: 'center', gap: 6,
        fontSize: 10.5, fontWeight: 700, letterSpacing: '0.08em',
        textTransform: 'uppercase', color: '#a1a1c2',
        fontFamily: 'DM Sans, sans-serif', marginBottom: 10,
      }}>
        {icon && <span style={{ fontSize: 13 }}>{icon}</span>}
        {title}
      </div>
    )}
    {children}
  </div>
);

// ─── Data row ─────────────────────────────────────────────────────────────────
const Row = ({ label, value }) => (
  <div style={{
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '5px 0', borderBottom: '1px solid #f3f4f6', gap: 12,
    fontSize: 12.5, fontFamily: 'DM Sans, sans-serif',
  }}>
    <span style={{ color: '#9ca3af', flexShrink: 0 }}>{label}</span>
    <span style={{ color: '#1e1b4b', fontWeight: 500, textAlign: 'right' }}>{value ?? '—'}</span>
  </div>
);

// ─── Stat tile ────────────────────────────────────────────────────────────────
const StatTile = ({ label, value, sub, color, bg, icon }) => (
  <div style={{
    background: bg || '#f8f9ff',
    borderRadius: 10,
    padding: '10px 12px',
    display: 'flex', flexDirection: 'column', gap: 2,
    border: '1px solid #ebebf5',
  }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
      <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#a1a1c2', fontFamily: 'DM Sans, sans-serif' }}>
        {label}
      </span>
      {icon && <span style={{ fontSize: 16, opacity: 0.7 }}>{icon}</span>}
    </div>
    <div style={{ fontSize: 18, fontWeight: 700, color: color || '#1e1b4b', fontFamily: 'Sora, sans-serif', lineHeight: 1.1 }}>
      {value}
    </div>
    {sub && <div style={{ fontSize: 10.5, color: '#9ca3af', fontFamily: 'DM Sans, sans-serif' }}>{sub}</div>}
  </div>
);

// ─── Expiry badge ─────────────────────────────────────────────────────────────
const ExpiryBadge = ({ dateStr }) => {
  if (!dateStr) return <span style={{ color: '#9ca3af' }}>—</span>;
  const date = new Date(dateStr);
  const daysLeft = Math.ceil((date - new Date()) / (1000 * 60 * 60 * 24));
  const expired = daysLeft < 0;
  const soon = daysLeft >= 0 && daysLeft <= 90;

  let bg = '#ecfdf5', color = '#065f46', border = '#6ee7b7', icon = '✓';
  if (expired) { bg = '#fef2f2'; color = '#991b1b'; border = '#fca5a5'; icon = '✕'; }
  else if (soon) { bg = '#fffbeb'; color = '#92400e'; border = '#fde68a'; icon = '⚠'; }

  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      padding: '2px 8px', borderRadius: 9999, fontSize: 11, fontWeight: 600,
      background: bg, color, border: `1px solid ${border}`,
      fontFamily: 'DM Sans, sans-serif',
    }}>
      {icon} {date.toLocaleDateString()}
      {soon && !expired && ` · ${daysLeft}d left`}
      {expired && ' · Expired'}
    </span>
  );
};

// ─── Main ─────────────────────────────────────────────────────────────────────
const Product = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const { tenant } = useTenant();
  const { addToCart, items } = useCart();

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [flashAdded, setFlashAdded] = useState(false);
  const [ads, setAds]         = useState([]);
  const [adsLoading, setAdsLoading] = useState(true);

  useEffect(() => {
    if (!tenant?.id || !productId) return;
    setLoading(true);
    fetchProduct(tenant.id, productId)
      .then(p => { if (!p) throw new Error('Product not found.'); setProduct(p); })
      .catch(e => setError(e.message || 'Failed to load product.'))
      .finally(() => setLoading(false));
  }, [tenant?.id, productId]);

  useEffect(() => {
    if (!tenant?.id) return;
    setAdsLoading(true);
    getAds(tenant.id, 'PRODUCT_PAGE')
      .then(setAds)
      .finally(() => setAdsLoading(false));
  }, [tenant?.id]);

  const handleAddToCart = () => {
    if (!product?.inStock) return;
    addToCart(product);
    setFlashAdded(true);
    setTimeout(() => setFlashAdded(false), 1500);
  };

  // ── Loading ────────────────────────────────────────────────────────────────
  if (loading) return (
    <div className="page">
      <main className="page-main" style={{ maxWidth: 940 }}>
        <div className="skeleton skeleton-line" style={{ width: 200, height: 12, marginBottom: 20 }} />
        <div className="skeleton" style={{ height: 100, borderRadius: 14, marginBottom: 12 }} />
        <div style={{ display: 'grid', gridTemplateColumns: '210px 1fr', gap: 12 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {[120, 80, 80].map((h, i) => <div key={i} className="skeleton" style={{ height: h, borderRadius: 12 }} />)}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {[100, 120, 80].map((h, i) => <div key={i} className="skeleton" style={{ height: h, borderRadius: 12 }} />)}
          </div>
        </div>
      </main>
    </div>
  );

  // ── Error ──────────────────────────────────────────────────────────────────
  if (error || !product) return (
    <div className="page">
      <main className="page-main">
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '60px 0', gap: 10 }}>
          <span style={{ fontSize: 36 }}>⚠️</span>
          <p style={{ fontSize: 15, fontWeight: 600, color: '#1e1b4b', margin: 0, fontFamily: 'DM Sans, sans-serif' }}>
            {error || 'Product not found.'}
          </p>
          <button className="btn-outline" onClick={() => navigate('/services/pharmacy')} style={{ marginTop: 6 }}>
            ← Back to Pharmacy
          </button>
        </div>
      </main>
    </div>
  );

  const medicineTypeMeta = MEDICINE_TYPES.find(m => m.value === product.medicineType);
  const discountPct = product.mrp && product.price
    ? Math.round(((product.mrp - product.price) / product.mrp) * 100) : null;
  const tags = product.tags
    ? (typeof product.tags === 'string' ? product.tags.split('|').map(t => t.trim()).filter(Boolean) : product.tags)
    : [];
  const cartItem = items.find(i => i.id === product.id);
  const inCart = !!cartItem;

  return (
    <>
      <style>{`
        .prod-wrap { max-width: 940px; padding: 12px 16px 28px; }

        .prod-hero {
          background: linear-gradient(135deg, #eef2ff 0%, #ede9fe 60%, #faf5ff 100%);
          border-radius: 14px; border: 1px solid #e0e7ff;
          padding: 18px 20px; display: flex; align-items: center;
          gap: 16px; margin-bottom: 14px; flex-wrap: wrap;
        }
        .prod-hero-icon {
          width: 64px; height: 64px; border-radius: 16px;
          background: #fff; border: 1px solid #e0e7ff;
          display: flex; align-items: center; justify-content: center;
          font-size: 34px; flex-shrink: 0;
          box-shadow: 0 2px 8px rgba(99,102,241,0.10);
        }
        .prod-hero-name {
          font-family: 'Sora', sans-serif; font-weight: 700;
          font-size: 18px; color: #1e1b4b; line-height: 1.2; margin: 0 0 3px;
        }
        .prod-hero-brand {
          font-family: 'DM Sans', sans-serif; font-size: 12.5px;
          color: #6d6aa0; margin: 0 0 7px;
        }
        .prod-hero-badges { display: flex; flex-wrap: wrap; gap: 5px; align-items: center; }
        .prod-hero-meta { flex: 1; min-width: 180px; }
        .prod-hero-price { margin-left: auto; text-align: right; flex-shrink: 0; }
        .prod-hero-price-val {
          font-family: 'Sora', sans-serif; font-weight: 700;
          font-size: 26px; color: #1e1b4b; line-height: 1;
        }
        .prod-hero-price-mrp {
          font-size: 12px; color: #9ca3af; text-decoration: line-through;
          font-family: 'DM Sans', sans-serif; margin-top: 2px;
        }
        .prod-hero-price-discount {
          display: inline-block; background: #dcfce7; color: #166534;
          border-radius: 9999px; font-size: 10.5px; font-weight: 700;
          padding: 2px 7px; margin-top: 4px; font-family: 'DM Sans', sans-serif;
        }

        .stats-bar {
          display: grid; grid-template-columns: repeat(4, 1fr);
          gap: 10px; margin-bottom: 14px;
        }
        .prod-grid {
          display: grid; grid-template-columns: 210px 1fr;
          gap: 12px; align-items: start;
        }
        .prod-left { display: flex; flex-direction: column; gap: 10px; }
        .prod-right { display: flex; flex-direction: column; gap: 10px; }
        .right-2col { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }

        .cart-action-btn {
          width: 100%; padding: 11px 14px; border-radius: 10px;
          font-size: 13.5px; font-weight: 700; cursor: pointer;
          font-family: 'DM Sans', sans-serif;
          display: flex; align-items: center; justify-content: center; gap: 7px;
          border: none; transition: opacity .15s, transform .1s;
        }
        .cart-action-btn:active { transform: scale(0.97); }

        @media (max-width: 720px) {
          .prod-grid { grid-template-columns: 1fr !important; }
          .right-2col { grid-template-columns: 1fr !important; }
          .stats-bar { grid-template-columns: repeat(2, 1fr) !important; }
          .prod-hero-price { margin-left: 0; text-align: left; width: 100%; }
        }
        @media (max-width: 440px) {
          .prod-hero { padding: 14px; gap: 12px; }
          .prod-hero-name { font-size: 15px; }
          .prod-hero-price-val { font-size: 20px; }
        }
      `}</style>

      <div className="page">
        <main className="page-main prod-wrap">

          {/* Breadcrumb */}
          <div className="breadcrumb" style={{ marginBottom: 12, fontSize: 12 }}>
            <button className="t-link" onClick={() => navigate('/')}>Home</button>
            <span>›</span>
            <button className="t-link" onClick={() => navigate('/services/pharmacy')}>Pharmacy</button>
            <span>›</span>
            <span className="bc-current" style={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'inline-block', verticalAlign: 'middle' }}>
              {product.name}
            </span>
          </div>

          {/* ── Hero strip ── */}
          <div className="prod-hero fade-up">
            <div className="prod-hero-icon">{product.emoji || '💊'}</div>

            <div className="prod-hero-meta">
              <p className="prod-hero-name">{product.name}</p>
              {product.brand && <p className="prod-hero-brand">{product.brand}</p>}
              <div className="prod-hero-badges">
                <StockBadge active={product.inStock} />
                {product.prescription && (
                  <span style={{ fontSize: 10.5, fontWeight: 700, padding: '3px 8px', borderRadius: 9999, background: '#fffbeb', color: '#d97706', border: '1px solid #fde68a', fontFamily: 'DM Sans, sans-serif' }}>
                    Rx Required
                  </span>
                )}
                {medicineTypeMeta && (
                  <span style={{ fontSize: 10.5, fontWeight: 500, padding: '3px 8px', borderRadius: 9999, background: '#f5f3ff', color: '#6d28d9', border: '1px solid #ddd6fe', fontFamily: 'DM Sans, sans-serif' }}>
                    {medicineTypeMeta.icon} {medicineTypeMeta.label}
                  </span>
                )}
                {(product.categoryLabel || product.categoryId) && (
                  <span style={{ fontSize: 10.5, fontWeight: 500, padding: '3px 8px', borderRadius: 9999, background: '#f0fdf4', color: '#166534', border: '1px solid #bbf7d0', fontFamily: 'DM Sans, sans-serif' }}>
                    📂 {product.categoryLabel || product.categoryId}
                  </span>
                )}
              </div>
            </div>

            <div className="prod-hero-price">
              <div className="prod-hero-price-val">₹{product.price ?? '—'}</div>
              {product.mrp && product.mrp !== product.price && (
                <div className="prod-hero-price-mrp">MRP ₹{product.mrp}</div>
              )}
              {discountPct > 0 && (
                <div className="prod-hero-price-discount">{discountPct}% off</div>
              )}
            </div>
          </div>

          {/* ── Ad / Campaign Banners ── */}
          <AdBanner ads={ads} loading={adsLoading} label="Sponsored" />

          {/* ── Stats bar ── */}
          <div className="stats-bar fade-up">
            <StatTile
              label="Price"
              value={product.price ? `₹${product.price}` : '—'}
              sub={product.unit ? `per ${product.unit}` : undefined}
              icon="💰"
              color="#1e1b4b"
            />
            <StatTile
              label="Availability"
              value={product.inStock ? 'In Stock' : 'Out of Stock'}
              sub={product.inStock ? 'Ready to order' : 'Currently unavailable'}
              icon="📦"
              color={product.inStock ? '#065f46' : '#991b1b'}
              bg={product.inStock ? '#f0fdf4' : '#fef2f2'}
            />
            <StatTile
              label="Rating"
              value={product.rating ? `${product.rating.toFixed(1)} ★` : 'No ratings'}
              sub={product.reviewCount ? `${product.reviewCount} reviews` : 'No reviews yet'}
              icon="⭐"
              color="#92400e"
              bg="#fffbeb"
            />
            <StatTile
              label="Discount"
              value={discountPct != null && discountPct > 0 ? `${discountPct}%` : '—'}
              sub={discountPct > 0 ? `Save ₹${product.mrp - product.price}` : 'No discount'}
              icon="🏷️"
              color={discountPct > 0 ? '#065f46' : '#9ca3af'}
              bg={discountPct > 0 ? '#f0fdf4' : '#f8f9ff'}
            />
          </div>

          {/* ── Main grid ── */}
          <div className="prod-grid fade-up">

            {/* LEFT */}
            <div className="prod-left">

              {/* Add to cart */}
              <Card>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {!product.inStock ? (
                    <button disabled className="cart-action-btn" style={{ background: '#f3f4f6', color: '#9ca3af', cursor: 'not-allowed' }}>
                      Out of Stock
                    </button>
                  ) : flashAdded ? (
                    <button disabled className="cart-action-btn" style={{ background: '#ecfdf5', color: '#065f46' }}>
                      ✓ Added to Cart
                    </button>
                  ) : inCart ? (
                    <>
                      <button className="cart-action-btn product-cart-btn-incart" onClick={handleAddToCart}>
                        + Add More ({cartItem.quantity} in cart)
                      </button>
                      <button className="cart-action-btn" onClick={() => navigate('/cart')}
                        style={{ background: '#1e1b4b', color: '#fff' }}>
                        🛒 View Cart
                      </button>
                    </>
                  ) : (
                    <button
                      className="cart-action-btn"
                      onClick={handleAddToCart}
                      style={{
                        background: product.prescription
                          ? 'linear-gradient(135deg, #dc2626, #b91c1c)'
                          : 'linear-gradient(135deg, #4f46e5, #7c3aed)',
                        color: '#fff',
                      }}
                    >
                      {product.prescription ? '+ Add Rx to Cart' : '🛒 Add to Cart'}
                    </button>
                  )}

                  {product.prescription && (
                    <div className="strip-info strip-info-product" style={{ marginTop: 2 }}>
                      <span>📋</span>
                      <span className="rx-pharmacist">Prescription required at checkout</span>
                    </div>
                  )}
                </div>
              </Card>

              {/* Product details */}
              <Card title="Details" icon="📋">
                <Row label="Brand" value={product.brand || '—'} />
                <Row label="Unit" value={product.unit || '—'} />
                <Row label="Medicine Type" value={medicineTypeMeta ? `${medicineTypeMeta.icon} ${medicineTypeMeta.label}` : product.medicineType || '—'} />
                <Row label="Category" value={product.categoryLabel || product.categoryId || '—'} />
                <Row label="Prescription" value={
                  product.prescription
                    ? <span style={{ color: '#dc2626', fontWeight: 600, fontSize: 11.5 }}>Rx Required</span>
                    : <span style={{ color: '#059669', fontSize: 11.5 }}>Not required</span>
                } />
                <Row label="Expiry" value={<ExpiryBadge dateStr={product.expiryDate} />} />
              </Card>

              {/* Tags */}
              {tags.length > 0 && (
                <Card title="Tags" icon="🏷️">
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                    {tags.map((tag, i) => (
                      <span key={i} style={{
                        fontSize: 11, fontWeight: 500, padding: '3px 9px',
                        borderRadius: 9999, background: '#eef2ff', color: '#4f46e5',
                        border: '1px solid #c7d2fe', fontFamily: 'DM Sans, sans-serif',
                      }}>{tag}</span>
                    ))}
                  </div>
                </Card>
              )}
            </div>

            {/* RIGHT */}
            <div className="prod-right">

              {/* Pricing */}
              <Card title="Pricing" icon="💵">
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
                  {[
                    { label: 'Selling Price', val: product.price ? `₹${product.price}` : '—', color: '#1e1b4b' },
                    { label: 'MRP', val: product.mrp ? `₹${product.mrp}` : '—', color: '#6b7280' },
                    { label: 'You Save', val: discountPct > 0 ? `₹${product.mrp - product.price}` : '—', color: discountPct > 0 ? '#059669' : '#9ca3af' },
                  ].map(({ label, val, color }) => (
                    <div key={label} style={{ background: '#f8f9ff', borderRadius: 9, padding: '10px 12px', textAlign: 'center', border: '1px solid #ebebf5' }}>
                      <div style={{ fontSize: 16, fontWeight: 700, color, fontFamily: 'Sora, sans-serif', marginBottom: 2 }}>{val}</div>
                      <div style={{ fontSize: 10, color: '#9ca3af', fontFamily: 'DM Sans, sans-serif', fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase' }}>{label}</div>
                    </div>
                  ))}
                </div>
                {discountPct > 0 && (
                  <div style={{ marginTop: 10, background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: '7px 11px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: 12, color: '#166534', fontFamily: 'DM Sans, sans-serif' }}>You save {discountPct}% vs MRP</span>
                    <span style={{ fontSize: 13, fontWeight: 700, color: '#065f46', fontFamily: 'Sora, sans-serif' }}>₹{product.mrp - product.price} off</span>
                  </div>
                )}
              </Card>

              {/* 2-col: Description + Ratings */}
              <div className="right-2col">
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {product.description && (
                    <Card title="Description" icon="📝" style={{ flex: 1 }}>
                      <p style={{
                        margin: 0, fontSize: 12.5, color: '#374151',
                        fontFamily: 'DM Sans, sans-serif', lineHeight: 1.65,
                      }}>{product.description}</p>
                    </Card>
                  )}

                  {product.prescription && (
                    <Card title="Prescription Info" icon="📋" accent="#f59e0b">
                      <p style={{ margin: 0, fontSize: 12.5, color: '#374151', fontFamily: 'DM Sans, sans-serif', lineHeight: 1.6 }}>
                        This is a prescription medicine. Please upload a valid prescription at checkout. Our pharmacist will verify before dispatch.
                      </p>
                    </Card>
                  )}
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <Card title="Ratings & Reviews" icon="⭐">
                    <div style={{ textAlign: 'center', padding: '6px 0 10px' }}>
                      <div style={{ fontSize: 32, fontWeight: 700, color: '#1e1b4b', fontFamily: 'Sora, sans-serif', lineHeight: 1 }}>
                        {product.rating ? product.rating.toFixed(1) : '—'}
                      </div>
                      {product.rating && (
                        <div style={{ fontSize: 16, color: '#f59e0b', letterSpacing: 2, margin: '4px 0 2px' }}>
                          {'★'.repeat(Math.round(product.rating))}{'☆'.repeat(5 - Math.round(product.rating))}
                        </div>
                      )}
                      <div style={{ fontSize: 11, color: '#9ca3af', fontFamily: 'DM Sans, sans-serif' }}>
                        {product.reviewCount ? `${product.reviewCount} reviews` : 'No reviews yet'}
                      </div>
                    </div>
                  </Card>

                  <Card title="Delivery & Safety" icon="🚚">
                    <Row label="Ships in" value="1–2 days" />
                    <Row label="Cold chain" value={product.medicineType === 'insulin' ? 'Required' : 'Not needed'} />
                    <Row label="Return policy" value="Non-returnable" />
                    <Row label="Genuine" value={<span style={{ color: '#059669', fontSize: 11.5, fontWeight: 600 }}>✓ Verified</span>} />
                  </Card>
                </div>
              </div>

              {/* New dataset fields — composition, usage, interactions, etc. */}
              {(product.composition || product.howToUse || product.howItWorks || product.commonSideEffects
                || product.alcoholInteraction || product.pregnancyInteraction || product.expertAdvice
                || product.medicineInteraction || product.productUse || product.faqForMedicine) && (
                <Card title="Medicine Information" icon="🧪">
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {[
                      { label: 'Composition',            value: product.composition },
                      { label: 'How to Use',              value: product.howToUse },
                      { label: 'How It Works',            value: product.howItWorks },
                      { label: 'Common Side Effects',     value: product.commonSideEffects },
                      { label: 'Alcohol Interaction',     value: product.alcoholInteraction },
                      { label: 'Pregnancy Interaction',   value: product.pregnancyInteraction },
                      { label: 'Expert Advice',           value: product.expertAdvice },
                      { label: 'Medicine Interaction',    value: product.medicineInteraction },
                      { label: 'Product Use',             value: product.productUse },
                      { label: 'FAQ',                     value: product.faqForMedicine },
                    ].filter(section => section.value).map(section => (
                      <div key={section.label}>
                        <div style={{
                          fontSize: 10.5, fontWeight: 700, letterSpacing: '0.05em',
                          textTransform: 'uppercase', color: '#a1a1c2',
                          fontFamily: 'DM Sans, sans-serif', marginBottom: 4,
                        }}>
                          {section.label}
                        </div>
                        <p style={{
                          margin: 0, fontSize: 12.5, color: '#374151',
                          fontFamily: 'DM Sans, sans-serif', lineHeight: 1.6,
                          whiteSpace: 'pre-wrap',
                        }}>{section.value}</p>
                      </div>
                    ))}
                  </div>
                </Card>
              )}
            </div>
          </div>
        </main>
      </div>
    </>
  );
};

export default Product;